import { useEffect } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { GPTModelItem } from "@/api/models"
import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

import { isCurrentModel } from "../components/reasoning-model-utils"
import { effortLabel } from "../utils/effort-label"
import {
  applyModelPick,
  findModelByFamily,
  matchModelToggleAction,
  resolveActiveEffort,
  stepEffort,
} from "../utils/model-toggle-shortcuts"

type UseModelToggleShortcutsProps = {
  /** Mirror of the pill's visibility/enabled state — false detaches the listener. */
  enabled: boolean
  availableModels: GPTModelItem[]
  /** The model that will actually run — its effort ladder is what Alt+± steps. */
  selectedModel?: GPTModelItem
  mode?: ReasoningMode
  effortOverride?: ReasoningEffortOverride | null
  onModelOverrideChange?: (modelId: string | null) => void
  onEffortOverrideChange?: (effort: ReasoningEffortOverride | null) => void
}

// Fixed id so rapid presses (stepping effort) update one toast instead of stacking.
const TOAST_ID = "model-toggle-shortcut"

/**
 * Global keyboard shortcuts for the composer's model selector: Alt+0–3 pick
 * Auto/Luna/Terra/Sol, Alt+4–6 a family's second (Global) deployment,
 * Alt+plus/minus step reasoning effort. Attached on
 * window so they work while the composer textarea has focus — Alt combos
 * never insert text there (AltGr ones do, and are excluded by the matcher).
 */
export function useModelToggleShortcuts({
  enabled,
  availableModels,
  selectedModel,
  mode,
  effortOverride,
  onModelOverrideChange,
  onEffortOverrideChange,
}: UseModelToggleShortcutsProps) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!enabled || !onModelOverrideChange) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const action = matchModelToggleAction(event)
      if (!action) return

      if (action.kind === "pick-model") {
        // Legacy ids could coincidentally contain a family name — only
        // current-tier models are shortcut targets, same as the picker's rows.
        const model = findModelByFamily(
          availableModels.filter(isCurrentModel),
          action.family,
          action.variant
        )
        if (!model) return
        event.preventDefault()
        // Same semantics as clicking the picker row — shared helper so the
        // effort-lock/reset rule can't drift between the two entry points.
        applyModelPick(model.id, mode, { onModelOverrideChange, onEffortOverrideChange })
        toast(
          t("reasoning_mode.shortcut_model_toast", {
            defaultValue: "Model: {{name}}",
            name: model.display_name,
          }),
          { id: TOAST_ID, duration: 1500 }
        )
        return
      }

      if (selectedModel?.id === "auto") {
        // The pill hides the slider in Auto (the router owns effort there);
        // the shortcut must not reintroduce a hidden override.
        event.preventDefault()
        toast(
          t("reasoning_mode.shortcut_effort_auto_toast", {
            defaultValue: "Auto picks the reasoning effort. Select a model to set it.",
          }),
          { id: TOAST_ID, duration: 1500 }
        )
        return
      }
      const supportedEfforts = selectedModel?.supported_efforts ?? []
      if (supportedEfforts.length === 0 || !onEffortOverrideChange) return
      event.preventDefault()
      const activeEffort = resolveActiveEffort(
        supportedEfforts,
        effortOverride,
        selectedModel?.default_effort
      )
      const nextEffort = stepEffort(supportedEfforts, activeEffort, action.direction)
      if (nextEffort) {
        onEffortOverrideChange(nextEffort)
      }
      // Clamped presses still toast the (unchanged) level — the effort lives
      // inside a closed dropdown, so the toast is the only feedback there is.
      const shownEffort = nextEffort ?? activeEffort
      if (shownEffort) {
        toast(
          t("reasoning_mode.shortcut_effort_toast", {
            defaultValue: "Reasoning effort: {{level}}",
            level: effortLabel(shownEffort, t),
          }),
          { id: TOAST_ID, duration: 1500 }
        )
      }
    }

    // Capture phase: the pill's effort-slider wrapper calls stopPropagation()
    // on keydown (to keep Radix menu roving focus off the slider), which would
    // starve a bubble-phase window listener exactly when the slider — the
    // control advertising Alt+± — has focus. Capture at window runs first and
    // cannot be stopped by bubble-phase handlers.
    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [
    enabled,
    availableModels,
    selectedModel,
    mode,
    effortOverride,
    onModelOverrideChange,
    onEffortOverrideChange,
    t,
  ])
}
